import {
    Filters,
    MatchingCategoryByIdResolvers,
    CategoryByIdResolvers,
    KeyFilters,
    MatchingCategoryByKeyFilterResolvers,
    emptyFilterMatchesOnlyEmptyKeys,
} from '../types/filters';
import { getCategoryByIdResolverLookup, getCategoryByKeyResolverLookup } from '../types/filters';
import { type ResourceLibraryItem, isAssetItem } from '../types/resourceLibraryItems';
import type { SortOrder, SortableProps } from '../types/sorting';
import type { SearchResult } from '../types/search';
import { sortItems } from '../tools/sorting';
import { filterItemsBySearchTerm } from '../tools/search';

// Utility function to add/remove values from arrays
export function toggleValueInArray(array: string[], value: string): string[] {
    if (array.includes(value)) {
        return array.filter((id) => id !== value);
    } else {
        return [...array, value];
    }
}

// Helper type to remove readonly modifiers.
type Mutable<T> = { -readonly [P in keyof T]: T[P] };

/**
 * Removes parent categories from the selected filters if their child categories are selected.
 *
 * In the UI, users must select a parent category first, which includes all items from its
 * subcategories. When users select specific subcategories, only those items should be displayed,
 * excluding the parent. This function ensures that parent categories are excluded from the filters
 * when their child categories are selected, preventing redundant filtering.
 */
export function getFiltersWithoutParentIds<F extends Filters>(
    filters: F,
    filterResolvers: MatchingCategoryByIdResolvers<F>
): Filters {
    const newFilters: Mutable<Filters> = { ...filters };

    for (const key of Object.keys(filters) as Array<keyof F>) {
        const value = filters[key];
        if (Array.isArray(value) && typeof key === 'string' && key.endsWith('Ids')) {
            const baseKey = key.slice(0, -3); // strip 'Ids'
            const resolverKey = (baseKey + 'ById') as keyof CategoryByIdResolvers;

            // Safely cast resolver
            const resolver = (filterResolvers as Partial<CategoryByIdResolvers>)[resolverKey];
            if (resolver) {
                // Narrow the resolver value to expected shape
                const cleaned = removeParentIds(
                    value as string[],
                    resolver as Record<string, { id: string; parent?: { id: string } }>
                );
                (newFilters as any)[key] = cleaned;
            }
        }
    }

    return newFilters as Filters;
}

// Generic helper function to remove parent IDs from a list of filter IDs
function removeParentIds<T extends { id: string; parent?: T }>(
    selectedIds: string[],
    itemsById: Record<string, T>
): string[] {
    const parentToChildrenMap = new Map<string, string[]>();

    // Create a map of parents to their children
    Object.values(itemsById).forEach((item) => {
        if (item.parent) {
            if (!parentToChildrenMap.has(item.parent.id)) {
                parentToChildrenMap.set(item.parent.id, []);
            }
            parentToChildrenMap.get(item.parent.id)!.push(item.id);
        }
    });

    // Remove parent IDs if any of their child IDs are also selected
    return selectedIds.filter((id) => {
        const children = parentToChildrenMap.get(id);
        if (children) {
            // If any child of the parent is selected, remove the parent
            return !children.some((childId) => selectedIds.includes(childId));
        }
        return true; // Keep the item if it has no children
    });
}

// Filter items by asset path; this works only for FileAsset items since InternalLinkAsset contain
// only IDs to internal links list and would have to be resolved first.
// TODO: Not being used anywhere except for in test. Is this due to it being deprecated? If so, please remove.
export function filterItemsByAssetPath(
    items: ResourceLibraryItem[],
    assetPaths: string[]
): ResourceLibraryItem[] {
    return items.filter(
        (item) => isAssetItem(item) && item.asset && assetPaths.includes(item.asset.fullpath)
    );
}

/**
 * Filter items with no filters and no sorting applied.
 *
 * Returns all items except those that must be filtered out due to empty filters;
 * i.e. applying filters with FilterDescriptor.emptyMeans = 'onlyEmpty'
 */
export function filterItemsUsingEmptyFilters<T extends ResourceLibraryItem>(
    items: T[],
    filters?: Filters
): T[] {
    const emptyFilterKeys = emptyFilterMatchesOnlyEmptyKeys.filter(
        (key) =>
            !filters ||
            !(key in filters) ||
            (Array.isArray(filters[key]) ? filters[key]!.length === 0 : !filters[key])
    );
    if (emptyFilterKeys.length === 0) {
        return items;
    }
    return items.filter((item) => {
        for (const filterKey of emptyFilterKeys) {
            const value = (item as any)[filterKey];
            if (Array.isArray(value) ? value.length > 0 : value) {
                return false;
            }
        }
        return true;
    });
}

export interface FilterItemsOptions<
    F extends Filters,
    Item extends ResourceLibraryItem = ResourceLibraryItem,
> {
    items: Item[];
    filters: F;
    sortOrder?: SortOrder;
    filterResolvers: MatchingCategoryByIdResolvers<F>;
    computeRelevance?: boolean;
    sortProps?: SortableProps[];
}

export function filterItemsWithRelevance<
    F extends Filters,
    Item extends ResourceLibraryItem = ResourceLibraryItem,
>({
    items,
    filters,
    sortOrder,
    filterResolvers,
    computeRelevance = true,
    sortProps,
}: FilterItemsOptions<F, Item>): SearchResult<Item>[] {
    if (!items) return [];

    // Get a copy of the filters without parent IDs.
    const filtersWithoutParents = getFiltersWithoutParentIds(filters, filterResolvers);

    const productGroupById = getCategoryByIdResolverLookup(filterResolvers, 'productGroupById');
    const productApplicationById = getCategoryByIdResolverLookup(
        filterResolvers,
        'productApplicationById'
    );
    const resourceTypeById = getCategoryByIdResolverLookup(filterResolvers, 'resourceTypeById');

    let filteredItems = filterItemsUsingEmptyFilters(items, filters).filter((item) => {
        // Filter by brandId.
        if (filtersWithoutParents.brandId && item.brandId !== filtersWithoutParents.brandId) {
            return false;
        }

        // Filter by productGroupIds (groups and parent groups).
        if (
            filtersWithoutParents.productGroupIds &&
            filtersWithoutParents.productGroupIds.length > 0 &&
            productGroupById &&
            !filtersWithoutParents.productGroupIds.some((id) =>
                item.productGroupIds.some((groupId) => {
                    const group = productGroupById[groupId];
                    return groupId === id || (group && group.parent?.id === id);
                })
            )
        ) {
            return false;
        }

        // Filter by productApplicationIds (applications and parent applications).
        if (
            filtersWithoutParents.productApplicationIds &&
            filtersWithoutParents.productApplicationIds.length > 0 &&
            productApplicationById &&
            !filtersWithoutParents.productApplicationIds.some((id) =>
                item.productApplicationIds.some((appId) => {
                    const app = productApplicationById[appId];
                    return appId === id || (app && app.parent?.id === id);
                })
            )
        ) {
            return false;
        }

        // Filter by resourceTypeIds (resource type and parent resource type).
        if (
            filtersWithoutParents.resourceTypeIds &&
            filtersWithoutParents.resourceTypeIds.length > 0 &&
            resourceTypeById &&
            !filtersWithoutParents.resourceTypeIds.some((id) => {
                const resourceType = resourceTypeById[item.resourceTypeId];
                return (
                    item.resourceTypeId === id || (resourceType && resourceType.parent?.id === id)
                );
            })
        ) {
            return false;
        }

        // Filter by navigationScopeIds.
        if (
            filtersWithoutParents.navigationScopeIds &&
            filtersWithoutParents.navigationScopeIds.length > 0 &&
            !filtersWithoutParents.navigationScopeIds.some((id) =>
                item.navigationScopeIds.includes(id)
            )
        ) {
            return false;
        }

        // Filter by exclusiveNavigationScopeIds.
        if (
            filtersWithoutParents.exclusiveNavigationScopeIds &&
            filtersWithoutParents.exclusiveNavigationScopeIds.length > 0 &&
            !filtersWithoutParents.exclusiveNavigationScopeIds.some((id) =>
                item.exclusiveNavigationScopeIds.includes(id)
            )
        ) {
            return false;
        }

        // Filter by assetPaths.
        if (filtersWithoutParents.assetPaths && filtersWithoutParents.assetPaths.length > 0) {
            if (
                !isAssetItem(item) ||
                !filtersWithoutParents.assetPaths.some(
                    (path) => item.asset && item.asset.fullpath.toLowerCase() === path.toLowerCase()
                )
            ) {
                return false;
            }
        }

        return true;
    });

    // If there is a search term, filter items and compute relevance scores.
    const searchTerm = filtersWithoutParents.searchTerm || null;
    const relevanceRequired = computeRelevance && sortOrder === 'relevance';
    let itemsWithRelevance = filterItemsBySearchTerm(filteredItems, searchTerm, relevanceRequired);

    // Apply sorting.
    if (sortOrder) {
        itemsWithRelevance = sortItems(itemsWithRelevance, sortOrder, sortProps);
    }

    return itemsWithRelevance;
}

// Convert filters using object keys to filters using numeric IDs
export function convertFilterKeysToFilters<F extends KeyFilters>(
    filters: F,
    keyFilterResolvers: MatchingCategoryByKeyFilterResolvers<F>
): Filters {
    const brandByKey = getCategoryByKeyResolverLookup(keyFilterResolvers, 'brandByKey');
    const productGroupByKey = getCategoryByKeyResolverLookup(
        keyFilterResolvers,
        'productGroupByKey'
    );
    const productApplicationByKey = getCategoryByKeyResolverLookup(
        keyFilterResolvers,
        'productApplicationByKey'
    );
    const resourceTypeByKey = getCategoryByKeyResolverLookup(
        keyFilterResolvers,
        'resourceTypeByKey'
    );

    const navigationNodeByPath = getCategoryByKeyResolverLookup(
        keyFilterResolvers,
        'navigationNodeByPath'
    );

    const brandId =
        filters.brandKey && brandByKey ? brandByKey[filters.brandKey]?.id ?? null : null;

    const productGroupIds =
        filters.productGroupKeys && productGroupByKey
            ? filters.productGroupKeys
                  .map((key) => productGroupByKey[key]?.id)
                  .filter((id): id is string => id !== null)
            : [];

    const productApplicationIds =
        filters.productApplicationKeys && productApplicationByKey
            ? filters.productApplicationKeys
                  .map((key) => productApplicationByKey[key]?.id)
                  .filter((id): id is string => id !== null)
            : [];

    const resourceTypeIds =
        filters.resourceTypeKeys && resourceTypeByKey
            ? filters.resourceTypeKeys
                  .map((key) => resourceTypeByKey[key]?.id)
                  .filter((id): id is string => id !== null)
            : [];

    const navigationScopeIds =
        filters.navigationScopePaths && navigationNodeByPath
            ? filters.navigationScopePaths
                  .map((path) => navigationNodeByPath[path]?.id)
                  .filter((id): id is string => id !== null)
            : [];

    const exclusiveNavigationScopeIds =
        filters.exclusiveNavigationScopePaths && navigationNodeByPath
            ? filters.exclusiveNavigationScopePaths
                  .map((path) => navigationNodeByPath[path]?.id)
                  .filter((id): id is string => id !== null)
            : [];

    const assetPaths = filters.resourceAssetPaths ? filters.resourceAssetPaths : [];
    const searchTerm = filters.searchTerm ?? null;

    return {
        brandId,
        productGroupIds,
        productApplicationIds,
        resourceTypeIds,
        navigationScopeIds,
        exclusiveNavigationScopeIds,
        assetPaths,
        searchTerm,
    };
}
